import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getBadgeColor, getAchievementRarity } from '../../config/badgeColors';
import AchievementImage from '../Achievements/AchievementImage';
import './AchievementToast.scss';

const AchievementToast = ({ achievement, onClose, autoClose = true, autoCloseDelay = 5000 }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Track closing animation timer to prevent memory leaks
  const closeTimerRef = useRef(null);

  // Trigger opening animation after component mounts
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    const animationFrame = requestAnimationFrame(() => {
      setIsAnimating(true);
    });
    
    return () => cancelAnimationFrame(animationFrame);
  }, []);


  // Handle close with proper timer management
  const handleClose = useCallback(() => {
    // Prevent multiple close calls
    if (closeTimerRef.current) return;
    
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete before calling onClose
  }, [onClose]);

  // Auto-close functionality
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(handleClose, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, handleClose]);

  // Cleanup the closing animation timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleToastClick = (e) => {
    // Prevent closing when clicking on the toast content
    e.stopPropagation();
  };

  // Calculate dynamic width class based on description length
  const getWidthClass = () => {
    const descLength = achievement.description?.length || 0;
    if (descLength <= 30) return 'achievement-toast--short';
    if (descLength <= 60) return 'achievement-toast--medium';
    return 'achievement-toast--long';
  };

  // Get badge color configuration and rarity
  const badgeColor = getBadgeColor(achievement);
  const rarity = getAchievementRarity(achievement);

  return (
    <div 
      className={`achievement-toast ${getWidthClass()} ${isClosing ? 'achievement-toast--closing' : isAnimating ? 'achievement-toast--opening' : ''}`}
      onClick={handleToastClick}
      style={{
        '--toast-color': badgeColor.background,
        '--toast-glow': badgeColor.glow
      }}
    >
      <div className="achievement-toast__content">
        <div className="achievement-toast__image-section">
          <div className="achievement-toast__icon">
            <AchievementImage
              filename={achievement.image_filename}
              alt={achievement.name}
              className="achievement-toast__image"
            />
          </div>
          <span 
            className={`achievement-toast__rarity achievement-toast__rarity--${rarity}`}
            style={{
              '--rarity-color': badgeColor.background,
              '--rarity-glow': badgeColor.glow
            }}
          >
            {rarity.toUpperCase()}
          </span>
        </div>
        
        <div className="achievement-toast__details">
          <div className="achievement-toast__header">
            <div className="achievement-toast__header-left">
              <h3 className="achievement-toast__title">Achievement Unlocked!</h3>
            </div>
            <button 
              className="achievement-toast__close-button"
              onClick={handleClose}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
          
          <h4 className="achievement-toast__name">{achievement.name}</h4>
          <p className="achievement-toast__description">{achievement.description}</p>
        </div>
      </div>
    </div>
  );
};

export default AchievementToast;